import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { CompanyStep, KindStep, PagesStep, PersonStep, RoleStep } from "@/components/user-wizard/WizardSteps";
import { DoneStep, ReviewStep } from "@/components/user-wizard/WizardFinish";
import { needsTypoConfirm } from "@/lib/emailTypos";

const TITLES = ["Who are you setting up?", "Their company", "Who will use it?", "Check and send"];
const BLANK = {
  kind: "", name: "", email: "", email_ok: "", role: "", landing_page_ids: [],
  company: { mode: "existing", id: "", name: "", contact_email: "", contact_email_ok: "", phone: "", states: [], price_per_lead: "" },
};
const BTN = "border border-black px-4 py-2 text-xs uppercase tracking-widest transition-colors duration-100 disabled:opacity-40";

function canNext(step, f) {
  const c = f.company;
  if (step === 0) return !!f.kind;
  if (step === 1) {
    if (f.kind === "employee") return !!f.role;
    if (f.kind === "media_buyer") return true;
    if (c.mode === "existing") return !!c.id;
    if (needsTypoConfirm(c.contact_email, c.contact_email_ok)) return false;
    if (f.kind === "affiliate") return c.name.trim().length >= 2;
    return c.name.trim().length >= 2 && c.states.length > 0 && Number(c.price_per_lead) > 0;
  }
  if (step === 2) return f.name.trim().length >= 2 && /.+@.+\..+/.test(f.email.trim()) && !needsTypoConfirm(f.email, f.email_ok);
  return true;
}

function summaryOf(f, opts) {
  const c = f.company;
  if (f.kind === "employee") return opts.employee_roles.find((r) => r.value === f.role)?.label;
  if (f.kind === "media_buyer") return `${f.landing_page_ids.length} landing page${f.landing_page_ids.length === 1 ? "" : "s"}`;
  if (c.mode === "new") return `${c.name} (new)`;
  return (f.kind === "buyer" ? opts.buyers : opts.affiliates).find((o) => o.id === c.id)?.name;
}

function payloadOf(f) {
  const c = f.company;
  const company = ["buyer", "affiliate"].includes(f.kind) ? {
    mode: c.mode, id: c.mode === "existing" ? c.id : null, name: c.name.trim() || null,
    contact_email: c.contact_email.trim() || null, phone: c.phone.trim() || null,
    states: c.states, price_per_lead: c.price_per_lead ? Number(c.price_per_lead) : null,
  } : null;
  return { kind: f.kind, name: f.name.trim(), email: f.email.trim(), role: f.role || null, company, landing_page_ids: f.landing_page_ids };
}

function StepBody({ step, form, opts, set, setCompany, pick }) {
  if (step === 0) return <KindStep allowed={opts.allowed} value={form.kind} onPick={pick} />;
  if (step === 1 && form.kind === "employee") return <RoleStep roles={opts.employee_roles} value={form.role} onChange={(role) => set({ role })} />;
  if (step === 1 && form.kind === "media_buyer") return <PagesStep pages={opts.pages} value={form.landing_page_ids} onChange={(ids) => set({ landing_page_ids: ids })} />;
  if (step === 1) return <CompanyStep kind={form.kind} opts={opts} c={form.company} set={setCompany} />;
  if (step === 2) return <PersonStep form={form} set={set} />;
  return <ReviewStep form={form} opts={opts} summary={summaryOf(form, opts)} />;
}

// Set up any login (buyer, affiliate, employee, media buyer) and email them how to sign in.
export function NewUserWizard({ open, onClose }) {
  const [opts, setOpts] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (!open) {
      return;
    }
    setStep(0);
    setForm(BLANK);
    setResult(null);
    api.get("/_admin/user-wizard/options").then((r) => setOpts(r.data)).catch(() => setOpts({ error: true }));
  }, [open]);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setCompany = (patch) => setForm((f) => ({ ...f, company: { ...f.company, ...patch } }));
  const pick = (kind) => {
    const list = kind === "buyer" ? opts.buyers : opts.affiliates;
    setForm({ ...BLANK, kind, company: { ...BLANK.company, mode: list?.length || kind === "employee" || kind === "media_buyer" ? "existing" : "new" } });
  };
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/_admin/user-wizard/accounts", payloadOf(form));
      setResult(data);
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't create the account");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none border-2 border-black sm:max-w-xl" data-testid="new-user-wizard">
        <DialogHeader>
          <div className="label-mono">{result ? "Done" : `New user · step ${step + 1} of 4`}</div>
          <DialogTitle className="font-chivo text-2xl font-black uppercase tracking-tight">{result ? "Account created" : TITLES[step]}</DialogTitle>
          <DialogDescription>{result && !result.email_sent ? "The welcome email didn't go out. Share the sign-in details below another way." : "They'll get an email with their username, a temporary password and how to get started."}</DialogDescription>
        </DialogHeader>
        {!opts && <div className="label-mono p-6 text-center">Loading…</div>}
        {opts?.error && <div className="text-sm text-red-700" data-testid="wizard-load-error">Couldn&apos;t load the wizard. You may not have permission to add users.</div>}
        {opts && !opts.error && (result ? <DoneStep result={result} setResult={setResult} />
          : <StepBody step={step} form={form} opts={opts} set={set} setCompany={setCompany} pick={pick} />)}
        <div className="mt-2 flex justify-between gap-2">
          {result ? (
            <>
              <button type="button" onClick={() => { setResult(null); setForm(BLANK); setStep(0); }} className={`${BTN} hover:bg-zinc-100`} data-testid="wizard-another">Set up another</button>
              <button type="button" onClick={onClose} className={`${BTN} bg-black text-white hover:bg-zinc-800`} data-testid="wizard-close">Close</button>
            </>
          ) : (
            <>
              <button type="button" disabled={step === 0} onClick={() => setStep((s) => s - 1)} className={`${BTN} hover:bg-zinc-100`} data-testid="wizard-back">Back</button>
              {step < 3 ? (
                <button type="button" disabled={!opts || opts.error || !canNext(step, form)} onClick={() => setStep((s) => s + 1)} className={`${BTN} bg-black text-white hover:bg-zinc-800`} data-testid="wizard-next">Next</button>
              ) : (
                <button type="button" disabled={busy} onClick={create} className={`${BTN} bg-black text-white hover:bg-zinc-800`} data-testid="wizard-create">{busy ? "Creating…" : "Create account & send email"}</button>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
